import { NextPageContext } from 'next'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { useEffect } from 'react'
import BasicHead from '../../../../components/common/BasicHead'
import CsrOnlyStudentBrazePixel from '../../../../components/common/CsrOnlyStudentBrazePixel'
import DeviceSplit from '../../../../components/common/DeviceSplit'
import LoadingSpinner from '../../../../components/common/LoadingSpinner'
import { getEventUserType, sendGTMBasicPageViewEvent } from '../../../../helpers/gtmHelper'
import { ISeoData } from '../../../../model/Common/common.interface'
import { logVisitEvent } from '../../../../modules/braze/logVisitRecord'
import { useCurrentUser } from '../../../../modules/context/UserContext'
import { RINGLE_DOMAIN } from '../../../../modules/envVars'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useRemoteConfigPopup from '../../../../modules/hooks/common/useRemoteConfigPopup'
import { LOCALE_KO } from '../../../../modules/i18n/config'
import { s_head } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { STUDENT_PAGE_VIEW_ID } from '../../../../modules/ids/gtm/student'
import { INVITE_PC_VER, INVITE_RINGLE_MEMBER_PC_VER } from '../../../../modules/images'
import { getOgUrl, t } from '../../../../modules/utils'
import { CDN_URL } from '../../../../modules/vars'

const HomeBodyMobile = dynamic(() => import('../../../../components/landing/body/mobile/HomeBodyV2'), {
  loading: () => (
    <div className="flex h-screen w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  ),
})
const HomeBodyDesktop = dynamic(() => import('../../../../components/landing/body/desktop/HomeBodyV2'), {
  loading: () => (
    <div className="flex h-screen w-screen items-center justify-center">
      <LoadingSpinner lg show primary />
    </div>
  ),
})

const Home = ({ ogData, seoData, isMobileServer }) => {
  const isMobile = useIsMobile(isMobileServer)
  const router = useRouter()
  const { currentUser } = useCurrentUser()
  const { locale } = useTranslation()

  useRemoteConfigPopup()

  useEffect(() => {
    sendGTMBasicPageViewEvent(
      STUDENT_PAGE_VIEW_ID.LANDING_HOME,
      getEventUserType(currentUser),
      locale,
      `Home`,
      router.asPath
    )

    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual'
    }

    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      {/* Head 관련 파일 */}
      <BasicHead ogData={ogData} seoData={seoData} />
      <CsrOnlyStudentBrazePixel
        currentUser={currentUser}
        callback={(appBoy) =>
          logVisitEvent(appBoy, 'view_landing', {
            property_1: 'home',
            locale: locale,
          })
        }
      />
      <DeviceSplit isMobile={isMobile} mobile={<HomeBodyMobile />} desktop={<HomeBodyDesktop />} />
    </>
  )
}

Home.getInitialProps = async (context: NextPageContext) => {
  const { referralCode, lang = LOCALE_KO } = context.query
  const path = context.asPath
  // /student/landing/referral 으로 들어올 경우 /student/landing/home 으로 rewrite 되며
  // 이 때 asPath는 /student/landing/referral으로 나온다.
  let isReferralPage = false
  let isSpecialReferralPage = false

  // /student/landing/referral 로 들어 왔거나
  // /student/landing/home?referralCode=1234 일 경우
  if (path.includes('/student/landing/referral') || referralCode) {
    isReferralPage = true
  }

  if (path.includes('/student/landing/special-referral')) {
    isSpecialReferralPage = true
  }

  // og data
  let ogData = {
    title: lang == LOCALE_KO ? '화상영어 영어회화 | 링글 일대일 화상영어' : t(s_head.student.title, lang),
    desc:
      lang == LOCALE_KO
        ? '화상영어 영어회화 No.1 일대일 화상영어 링글 스탠퍼드 MBA출신이 만든 1:1 화상 영어 명문대 원어민 튜터의 똑똑한 수업을 경험하세요!'
        : t(s_head.student.desc, lang),
    image: `${CDN_URL}/web/og_thumbnail.png`,
    ogUrl: getOgUrl({ lang, context }),
  }

  if (isSpecialReferralPage) {
    ogData = {
      title: lang == LOCALE_KO ? '친구가 링글을 선물했어요!' : 'A Friend Invited You to Ringle!',
      desc: lang == LOCALE_KO ? '지금 가입하고 바로 10만 포인트를 받아가세요.' : 'Sign up now and receive $76 points',
      image: INVITE_RINGLE_MEMBER_PC_VER(lang == LOCALE_KO ? 'KR' : 'EN'),
      ogUrl: `https://${RINGLE_DOMAIN}${path}`,
    }
  } else if (isReferralPage) {
    ogData = {
      title: lang == LOCALE_KO ? '친구가 링글을 선물했어요!' : 'A friend invited you to Ringle!',
      desc: lang == LOCALE_KO ? '선물이 사라지기 전에 확인하세요!' : 'Check out the invite before the gifts disappear!',
      image: INVITE_PC_VER(lang == LOCALE_KO ? 'KR' : 'EN'),
      ogUrl: `https://${RINGLE_DOMAIN}${path}`,
    }
  }

  const seoData: ISeoData = {
    pageTitle: lang == LOCALE_KO ? '화상영어 영어회화 | 링글 일대일 화상영어' : t(s_head.student.title, lang),
    pageDescription:
      lang == LOCALE_KO
        ? '화상영어 영어회화 No.1 일대일 화상영어 링글 스탠퍼드 MBA출신이 만든 1:1 화상 영어 명문대 원어민 튜터의 똑똑한 수업을 경험하세요!'
        : t(s_head.student.desc, lang),
    pageKeywords:
      lang === LOCALE_KO
        ? '화상영어, 영어회화, 전화영어, 영어문장, 성인영어회화, 원어민화상영어, 영어회화책, 성인화상영어, 영어회화공부, 영어회화스터디, 비즈니스영어, 영어잘하는법, 기본영어회화, 영국영어, 직장인영어회화, 비지니스영어, 원어민영어회화, 전화영어회화, 비즈니스영어회화, 화상영어사이트, 영어회화학원, 성인영어회화학원'
        : null,
    pageCanonicalPath: `https://${RINGLE_DOMAIN}`,
  }

  let userAgent: string

  if (context.req) {
    // if you are on the server and you get a 'req' property from your context
    userAgent = context.req.headers['user-agent'] // get the user-agent from the headers
  } else {
    userAgent = navigator.userAgent // if you are on the client you can access the navigator from the window object
  }

  const isMobileServer = Boolean(userAgent.match(/Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i))

  return {
    ogData,
    seoData,
    isMobileServer,
  }
}
export default Home
